import React, { useState, useCallback, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import { GridColDef } from '@mui/x-data-grid';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { FilterableDataTable, DrawerPanel, ConfirmDialog } from '../../components/shared';
import type { RowAction, DrawerAction } from '../../components/shared';
import { MachinePartControllerApi, MachineControllerApi } from '../../api-generated/apis';
import type { MachinePart, Machine } from '../../api-generated/models';
import { apiConfig } from '../../api-generated/apiConfig';

interface MachinePartRow {
  id: string | number;
  name: string;
  partNumber: string;
  machineId: number | null;
  machineName: string;
}

interface MachineOption {
  id: number;
  name: string;
}

const machinePartApi = new MachinePartControllerApi(apiConfig);
const machineApi = new MachineControllerApi(apiConfig);

const columns: GridColDef[] = [
  { field: 'name', headerName: 'Name', flex: 1, minWidth: 150 },
  { field: 'partNumber', headerName: 'Part Number', flex: 0.8, minWidth: 120 },
  { field: 'machineName', headerName: 'Machine', flex: 1, minWidth: 150 },
];

const MachinePartsPanel: React.FC = () => {
  const queryClient = useQueryClient();

  // Drawer state
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);

  // Form state
  const [formName, setFormName] = useState('');
  const [formPartNumber, setFormPartNumber] = useState('');
  const [formMachineId, setFormMachineId] = useState<number | null>(null);
  const [errors, setErrors] = useState<{ name?: string; partNumber?: string }>({});

  // Confirm dialog state
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);

  // Queries
  const { data: parts, isLoading } = useQuery<MachinePart[]>({
    queryKey: ['machine-parts'],
    queryFn: () => machinePartApi.getAllMachineParts(),
  });

  const machinesQuery = useQuery<Machine[]>({
    queryKey: ['machines', 'options'],
    queryFn: () => machineApi.getAllMachines(),
  });

  // Derived data
  const machineOptions: MachineOption[] = useMemo(
    () => (machinesQuery.data ?? []).map((m: any) => ({ id: Number(m.id) || 0, name: m.name ?? '' })),
    [machinesQuery.data],
  );

  const rows: MachinePartRow[] = useMemo(
    () => (parts ?? []).map((p: any) => ({
      id: Number(p.id) || 0,
      name: p.name ?? '',
      partNumber: p.partNumber ?? '',
      machineId: null,
      machineName: p.description ?? '—',
    })),
    [parts],
  );

  // Mutations
  const saveMutation = useMutation({
    mutationFn: (data: { id?: number; name: string; partNumber: string; machineId: number | null }) => {
      const payload: MachinePart = { name: data.name, partNumber: data.partNumber };
      if (data.id) {
        return machinePartApi.updateMachinePart({ id: String(data.id), machinePart: payload } as any);
      }
      return machinePartApi.createMachinePart({ machinePart: payload } as any);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['machine-parts'] });
      setDrawerOpen(false);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => machinePartApi.deleteMachinePart({ id: String(id) } as any),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['machine-parts'] });
      setConfirmOpen(false);
    },
  });

  // Handlers
  const handleAdd = useCallback(() => {
    setEditingId(null);
    setFormName('');
    setFormPartNumber('');
    setFormMachineId(null);
    setErrors({});
    setDrawerOpen(true);
  }, []);

  const handleEdit = useCallback((row: MachinePartRow) => {
    setEditingId(Number(row.id));
    setFormName(row.name);
    setFormPartNumber(row.partNumber);
    setFormMachineId(row.machineId);
    setErrors({});
    setDrawerOpen(true);
  }, []);

  const handleSave = useCallback(() => {
    const newErrors: { name?: string; partNumber?: string } = {};
    if (!formName.trim()) newErrors.name = 'Name is required';
    if (!formPartNumber.trim()) newErrors.partNumber = 'Part number is required';
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    saveMutation.mutate({
      id: editingId ?? undefined,
      name: formName.trim(),
      partNumber: formPartNumber.trim(),
      machineId: formMachineId,
    });
  }, [formName, formPartNumber, formMachineId, editingId, saveMutation]);

  // Row actions
  const rowActions: RowAction<MachinePartRow>[] = [
    { label: 'Edit', icon: EditIcon, onClick: handleEdit },
    { label: 'Delete', icon: DeleteIcon, onClick: (row) => { setDeleteId(Number(row.id)); setConfirmOpen(true); } },
  ];

  // Drawer actions
  const drawerActions: DrawerAction[] = [
    { label: 'Cancel', variant: 'outlined', onClick: () => setDrawerOpen(false) },
    { label: editingId ? 'Update' : 'Create', variant: 'contained', onClick: handleSave, disabled: !formName || !formPartNumber },
  ];

  return (
    <>
      <FilterableDataTable<MachinePartRow>
        rows={rows}
        columns={columns}
        loading={isLoading}
        totalCount={rows.length}
        page={0}
        pageSize={25}
        onPageChange={() => {}}
        onPageSizeChange={() => {}}
        actions={rowActions}
        primaryAction={{ label: 'Add Part', onClick: handleAdd }}
        searchPlaceholder="Search parts..."
      />
      <DrawerPanel
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        title={editingId ? 'Edit Part' : 'New Part'}
        width="narrow"
        actions={drawerActions}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            label="Name"
            value={formName}
            onChange={(e) => { setFormName(e.target.value); setErrors((prev) => ({ ...prev, name: undefined })); }}
            required
            error={!!errors.name}
            helperText={errors.name}
            fullWidth
            size="small"
          />
          <TextField
            label="Part Number"
            value={formPartNumber}
            onChange={(e) => { setFormPartNumber(e.target.value); setErrors((prev) => ({ ...prev, partNumber: undefined })); }}
            required
            error={!!errors.partNumber}
            helperText={errors.partNumber}
            fullWidth
            size="small"
          />
          <FormControl fullWidth size="small">
            <InputLabel id="part-machine-label">Machine</InputLabel>
            <Select
              labelId="part-machine-label"
              value={formMachineId ?? ''}
              label="Machine"
              onChange={(e: SelectChangeEvent<number | string>) => {
                const val = e.target.value;
                setFormMachineId(val === '' ? null : Number(val));
              }}
            >
              <MenuItem value=""><em>None</em></MenuItem>
              {machineOptions.map((m) => (
                <MenuItem key={m.id} value={m.id}>{m.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </DrawerPanel>
      <ConfirmDialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={() => deleteId != null && deleteMutation.mutate(deleteId)}
        title="Delete Part"
        message="Are you sure you want to delete this part? This action cannot be undone."
        confirmLabel="Delete"
        confirmColor="error"
      />
    </>
  );
};

export default MachinePartsPanel;
